/**
 * Platform detection utilities for ISOGyaan vs Skill Compliance
 */
import { getCurrentBranding } from "./branding.js";

// Platform types
export const PLATFORMS = {
  ISOGYAAN: "ISOGYAAN",
  SKILL_COMPLIANCE: "SKILL_COMPLIANCE",
  SKILL_COMPLIANCE_CA: "SKILL_COMPLIANCE_CA",
  PROCERT_COMPLIANCE: "PROCERT_COMPLIANCE",
};

export const normalizePlatformKey = (value) => {
  const compact = String(value || "ISOGYAAN")
    .trim()
    .toUpperCase()
    .replace(/[_\s-]/g, "");

  if (compact === "SKILLCOMPLIANCE") return PLATFORMS.SKILL_COMPLIANCE;
  if (compact === "SKILLCOMPLIANCECA") {
    return PLATFORMS.SKILL_COMPLIANCE_CA;
  }
  if (compact === "PROCERTCOMPLIANCE") {
    return PLATFORMS.PROCERT_COMPLIANCE;
  }
  return PLATFORMS.ISOGYAAN;
};

export const classifyAnalyticsPlatform = ({ platform, customDomain } = {}) => {
  if (customDomain) return "CUSTOM_DOMAINS";
  return normalizePlatformKey(platform);
};

// Currency mappings
export const CURRENCIES = {
  [PLATFORMS.ISOGYAAN]: "INR",
  [PLATFORMS.SKILL_COMPLIANCE]: "USD",
  [PLATFORMS.SKILL_COMPLIANCE_CA]: "CAD",
  [PLATFORMS.PROCERT_COMPLIANCE]: "USD",
};

// Currency symbols
export const CURRENCY_SYMBOLS = {
  INR: "₹",
  USD: "$",
  CAD: "C$",
};

// Payment gateway mapping per platform
export const PAYMENT_GATEWAYS = {
  [PLATFORMS.ISOGYAAN]: "razorpay",
  [PLATFORMS.SKILL_COMPLIANCE]: "stripe",
  [PLATFORMS.SKILL_COMPLIANCE_CA]: "stripe",
  [PLATFORMS.PROCERT_COMPLIANCE]: "stripe",
};

// Tax configuration per platform
// IMPORTANT: Keep rates in sync with supabase/functions/_shared/tax-config.ts
export const TAX_CONFIG = {
  [PLATFORMS.ISOGYAAN]: {
    rate: 0.18, // 18% GST
    label: "GST",
    displayText: "+ taxes",
    included: false, // Tax not included in displayed price
  },
  [PLATFORMS.SKILL_COMPLIANCE]: {
    rate: 0,
    label: "Stripe Tax",
    displayText: "tax calculated at checkout",
    included: false,
  },
  [PLATFORMS.SKILL_COMPLIANCE_CA]: {
    rate: 0,
    label: "Stripe Tax",
    displayText: "tax calculated at checkout",
    included: false,
  },
  [PLATFORMS.PROCERT_COMPLIANCE]: {
    rate: 0,
    label: "Stripe Tax",
    displayText: "tax calculated at checkout",
    included: false,
  },
};

const getCurrencyForPlatform = (platform) => {
  switch (platform) {
    case PLATFORMS.SKILL_COMPLIANCE_CA:
      return "CAD";
    case PLATFORMS.SKILL_COMPLIANCE:
    case PLATFORMS.PROCERT_COMPLIANCE:
      return "USD";
    case PLATFORMS.ISOGYAAN:
    default:
      return "INR";
  }
};

const getCurrencySymbol = (currency) => {
  switch (currency) {
    case "INR":
      return "₹";
    case "CAD":
      return "C$";
    case "USD":
    default:
      return "$";
  }
};

const getCurrencyLocale = (currency) => {
  switch (currency) {
    case "INR":
      return "en-IN";
    case "CAD":
      return "en-CA";
    case "USD":
    default:
      return "en-US";
  }
};

const getPaymentGatewayForPlatform = (platform) => {
  switch (platform) {
    case PLATFORMS.ISOGYAAN:
      return "razorpay";
    case PLATFORMS.SKILL_COMPLIANCE:
    case PLATFORMS.SKILL_COMPLIANCE_CA:
    case PLATFORMS.PROCERT_COMPLIANCE:
    default:
      return "stripe";
  }
};

const getTaxConfigForPlatform = (platform) => {
  switch (platform) {
    case PLATFORMS.SKILL_COMPLIANCE:
      return TAX_CONFIG.SKILL_COMPLIANCE;
    case PLATFORMS.SKILL_COMPLIANCE_CA:
      return TAX_CONFIG.SKILL_COMPLIANCE_CA;
    case PLATFORMS.PROCERT_COMPLIANCE:
      return TAX_CONFIG.PROCERT_COMPLIANCE;
    case PLATFORMS.ISOGYAAN:
    default:
      return TAX_CONFIG.ISOGYAAN;
  }
};

const getTaxConfigForCurrency = (currency) => {
  switch (currency) {
    case "CAD":
      return TAX_CONFIG.SKILL_COMPLIANCE_CA;
    case "USD":
      return TAX_CONFIG.SKILL_COMPLIANCE;
    case "INR":
      return TAX_CONFIG.ISOGYAAN;
    default:
      return null;
  }
};

/**
 * Detect platform based on hostname/domain
 * @returns {string} Platform constant
 */
export const getCurrentPlatform = () => {
  const host = typeof window === 'undefined' ? '' : window.location.hostname.toLowerCase().replace(/^www\./, '');
  if (typeof document !== 'undefined' && (document.querySelector('meta[name="x-partner-slug"]') || document.querySelector('meta[name="x-white-label"]')?.content === 'true' || window.location.pathname?.startsWith('/w/'))) return PLATFORMS.PROCERT_COMPLIANCE;
  if (host === 'isogyaan.com') return PLATFORMS.ISOGYAAN;
  if (host === 'skillcompliance.com') return PLATFORMS.SKILL_COMPLIANCE;
  if (host === 'skillcompliance.ca') return PLATFORMS.SKILL_COMPLIANCE_CA;
  if (host === 'procertcompliance.com' || host.endsWith('.procertcompliance.com')) return PLATFORMS.PROCERT_COMPLIANCE;
  if (host && host !== 'localhost' && host !== '127.0.0.1' && !host.endsWith('.netlify.app')) return PLATFORMS.PROCERT_COMPLIANCE;
  // Check for Vite environment variable (set via npm scripts)
  const envPlatform = import.meta.env?.VITE_PLATFORM;
  if (
    envPlatform === "skillcompliance-ca" ||
    envPlatform === "cad" ||
    envPlatform === "ca"
  ) {
    return PLATFORMS.SKILL_COMPLIANCE_CA;
  }
  if (envPlatform === "skillcompliance" || envPlatform === "usd") {
    return PLATFORMS.SKILL_COMPLIANCE;
  }
  if (envPlatform === "isogyaan" || envPlatform === "inr") {
    return PLATFORMS.ISOGYAAN;
  }

  // Check for ProCert development mode on localhost
  const isProCertDev = import.meta.env?.VITE_SHOW_PROCERT_HOME === "true";
  if (
    typeof window !== "undefined" &&
    window.location.hostname === "localhost" &&
    isProCertDev
  ) {
    return PLATFORMS.PROCERT_COMPLIANCE;
  }

  // Partner custom domains — the edge function injects a meta tag so we can
  // identify them regardless of TLD. Must run before hostname heuristics so a
  // partner whose custom domain ends in ".ca" isn't misread as SKILL_COMPLIANCE_CA.
  if (typeof document !== "undefined") {
    const whiteLabel = document.querySelector('meta[name="x-white-label"]');
    if (whiteLabel && whiteLabel.content === "true") {
      return PLATFORMS.PROCERT_COMPLIANCE;
    }
  }

  // Check hostname for production domains
  const hostname = window.location.hostname.toLowerCase();

  // Check for ProCertCompliance domain first
  if (hostname.includes("procertcompliance")) {
    return PLATFORMS.PROCERT_COMPLIANCE;
  }
  if (hostname.endsWith(".ca") || hostname.includes("skillcompliance.ca")) {
    return PLATFORMS.SKILL_COMPLIANCE_CA;
  }
  if (hostname.includes("skillcompliance")) {
    return PLATFORMS.SKILL_COMPLIANCE;
  }

  // Default to ISOGyaan for localhost and other domains
  return PLATFORMS.ISOGYAAN;
};

// Module-level currency override — set by partner stores so every price
// function automatically resolves to the partner's currency without needing
// an explicit override parameter threaded through every call.
let _currencyOverride = null;

/**
 * Set a global currency override (call from partner/workspace layouts).
 * Pass null to clear.
 */
export const setCurrencyOverride = (currency) => {
  _currencyOverride = currency || null;
};

/**
 * Get the active currency override (if any).
 * @returns {string|null}
 */
export const getCurrencyOverride = () => _currencyOverride;

/**
 * Get currency for current platform (respects partner override)
 * @returns {string} Currency code ('INR', 'USD', or 'CAD')
 */
export const getCurrentCurrency = () => {
  return _currencyOverride || getCurrencyForPlatform(getCurrentPlatform());
};

/**
 * Get currency symbol for current platform
 * @returns {string} Currency symbol ('₹', '$', or 'C$')
 */
export const getCurrentCurrencySymbol = () => {
  return getCurrencySymbol(getCurrentCurrency());
};

/**
 * Get appropriate price for current platform
 * @param {Object} course - Course object with price_inr and price_usd
 * @returns {number} Price for current platform
 */
export const getPriceForCurrentPlatform = (course, overrideCurrency = null) => {
  const currency = overrideCurrency || getCurrentCurrency();

  if (currency === "CAD") {
    return course?.price_cad || 0;
  }
  if (currency === "USD") {
    return course?.price_usd || 0;
  }
  return course?.price_inr || 0;
};

/**
 * Format price for current platform
 * Uses currency-based locale lookup so all platforms (including ProCert) format correctly.
 * @param {number} price - Price value
 * @param {string|null} overrideCurrency - Optional currency override
 * @returns {string} Formatted price string
 */
export const formatPrice = (price, overrideCurrency = null) => {
  const currency = overrideCurrency || getCurrentCurrency();
  const symbol = getCurrencySymbol(currency);
  const locale = getCurrencyLocale(currency);

  // Whole amounts stay without decimals; an amount with cents/paise shows
  // them instead of being rounded (a $49.99 course is not "$50").
  const amount = Number(price);
  const hasFraction =
    Number.isFinite(amount) && Math.round(amount * 100) % 100 !== 0;
  const numberFormatted = new Intl.NumberFormat(locale, {
    style: "decimal",
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: hasFraction ? 2 : 0,
  }).format(price);

  return `${symbol}${numberFormatted}`;
};

/**
 * Get formatted price for course based on current platform
 * @param {Object} course - Course object
 * @returns {string} Formatted price string
 */
export const getFormattedPriceForCourse = (course, overrideCurrency = null) => {
  const price = getPriceForCurrentPlatform(course, overrideCurrency);
  return formatPrice(price, overrideCurrency);
};

/**
 * Format a monetary amount with 2 decimal places (for financial / payout displays).
 * @param {number} amount - Amount to format
 * @param {string|null} overrideCurrency - Optional currency override
 * @returns {string} e.g. "₹1,234.56" or "C$99.00"
 */
export const formatCurrencyAmount = (amount, overrideCurrency = null) => {
  const currency = overrideCurrency || getCurrentCurrency();
  const symbol = getCurrencySymbol(currency);
  const locale = getCurrencyLocale(currency);
  const formatted = new Intl.NumberFormat(locale, {
    style: "decimal",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(amount || 0));
  return `${symbol}${formatted}`;
};

/**
 * Check if we're on the root ProCertCompliance domain (not a partner subdomain).
 * Root domain should not show prices or allow enrollment — purchases happen via partners.
 * @returns {boolean}
 */
export const isProCertRootDomain = () => {
  const hostname = window.location.hostname.toLowerCase();
  const PARTNER_DOMAIN = "procertcompliance.com";
  const isRoot =
    hostname === PARTNER_DOMAIN || hostname === `www.${PARTNER_DOMAIN}`;
  const isLocalDev =
    hostname === "localhost" &&
    import.meta.env?.VITE_SHOW_PROCERT_HOME === "true";
  return isRoot || isLocalDev;
};

/**
 * Check if current platform is ISOGyaan
 * @returns {boolean}
 */
export const isISOGyaan = () => {
  return getCurrentPlatform() === PLATFORMS.ISOGYAAN;
};

/**
 * Check if current platform is Skill Compliance
 * @returns {boolean}
 */
export const isSkillCompliance = () => {
  return getCurrentPlatform() === PLATFORMS.SKILL_COMPLIANCE;
};

/**
 * Check if current platform uses Razorpay for payments
 * @returns {boolean}
 */
export const useRazorpay = () => {
  return getPaymentGatewayForPlatform(getCurrentPlatform()) === "razorpay";
};

/**
 * Check if current platform uses Stripe for payments
 * @returns {boolean}
 */
export const useStripe = () => {
  return getPaymentGatewayForPlatform(getCurrentPlatform()) === "stripe";
};

/**
 * Get platform branding
 * @returns {Object} Branding object from branding.js
 */
export const getPlatformBranding = () => {
  return getCurrentBranding();
};

/**
 * Get platform name
 * @returns {string} Platform name
 */
export const getPlatformName = () => {
  return getPlatformBranding().name;
};

/**
 * Get platform tagline
 * @returns {string} Platform tagline
 */
export const getPlatformTagline = () => {
  return getPlatformBranding().tagline;
};

// ===========================================
// TAX UTILITIES
// ===========================================

/**
 * Get tax configuration for current platform
 * @returns {Object} Tax config object with rate, label, displayText, included
 */
export const getTaxConfig = (overrideCurrency = null) => {
  const currency = overrideCurrency || _currencyOverride;
  const currencyTaxConfig = getTaxConfigForCurrency(currency);
  if (currencyTaxConfig) {
    return currencyTaxConfig;
  }
  return getTaxConfigForPlatform(getCurrentPlatform());
};

/**
 * Get tax rate for current platform
 * @returns {number} Tax rate as decimal (e.g., 0.13 for 13%)
 */
export const getTaxRate = (overrideCurrency = null) => {
  return getTaxConfig(overrideCurrency).rate;
};

/**
 * Get tax display text for current platform (e.g., "+13% HST")
 * @returns {string} Tax display text, or empty string if no tax
 */
export const getTaxDisplayText = (overrideCurrency = null) => {
  return getTaxConfig(overrideCurrency).displayText;
};

/**
 * Check if tax is applicable for current platform
 * @returns {boolean} True if tax should be displayed
 */
export const hasTax = (overrideCurrency = null) => {
  const config = getTaxConfig(overrideCurrency);
  return config.rate > 0 && !config.included;
};

/**
 * Whether a tax note belongs next to displayed prices: "+ taxes" where GST is
 * added on top, "tax calculated at checkout" where Stripe Tax computes it.
 * Unlike hasTax(), this is true on USD/CAD even though no fixed rate applies.
 * @returns {boolean}
 */
export const hasTaxNote = (overrideCurrency = null) => {
  const config = getTaxConfig(overrideCurrency);
  return Boolean(config.displayText) && !config.included;
};

/**
 * Calculate tax amount for a given price
 * @param {number} price - Base price
 * @returns {number} Tax amount
 */
export const calculateTax = (price, overrideCurrency = null) => {
  return price * getTaxRate(overrideCurrency);
};

/**
 * Get total price including tax
 * @param {number} price - Base price
 * @returns {number} Total price with tax
 */
export const getPriceWithTax = (price, overrideCurrency = null) => {
  return price + calculateTax(price, overrideCurrency);
};

/**
 * Get formatted price with tax indicator for display
 * @param {Object} course - Course object
 * @returns {string} Formatted price with tax text (e.g., "C$299 +13% HST")
 */
export const getFormattedPriceWithTax = (course) => {
  const basePrice = getFormattedPriceForCourse(course);
  const taxText = getTaxDisplayText();

  if (taxText) {
    return `${basePrice} ${taxText}`;
  }
  return basePrice;
};

/**
 * Get price breakdown object for detailed display
 * @param {Object} course - Course object
 * @returns {Object} Object with basePrice, taxAmount, totalPrice, taxText, hasTax
 */
export const getPriceBreakdown = (course) => {
  const basePrice = getPriceForCurrentPlatform(course);
  const taxAmount = calculateTax(basePrice);
  const totalPrice = basePrice + taxAmount;
  const config = getTaxConfig();

  return {
    basePrice,
    basePriceFormatted: formatPrice(basePrice),
    taxAmount,
    taxAmountFormatted: formatPrice(taxAmount),
    totalPrice,
    totalPriceFormatted: formatPrice(totalPrice),
    taxRate: config.rate,
    taxLabel: config.label,
    taxDisplayText: config.displayText,
    hasTax: config.rate > 0 && !config.included,
  };
};

// ===========================================
// ACCREDITATION PRICING
// ===========================================

/**
 * Get the price for a course based on accreditation type.
 * @param {object} course - Course object with price fields
 * @param {'accredited' | 'non_accredited'} accreditationType
 * @returns {number} Price for the current platform adjusted for accreditation
 */
const getNonExemplarPrice = (course, overrideCurrency = null) => {
  const currency = (overrideCurrency || getCurrentCurrency()).toUpperCase();
  if (currency === "CAD") return course?.non_exemplar_price_cad || 0;
  if (currency === "USD") return course?.non_exemplar_price_usd || 0;
  return course?.non_exemplar_price_inr || 0;
};

export const getAccreditationPrice = (
  course,
  accreditationType = "accredited",
  overrideCurrency = null,
) => {
  const basePrice = getPriceForCurrentPlatform(course, overrideCurrency);
  if (accreditationType === "non_accredited") {
    return getNonExemplarPrice(course, overrideCurrency);
  }
  return basePrice;
};

/**
 * Refresher course discount rate (30% off = multiply by 0.7)
 */
export const REFRESHER_DISCOUNT = 0.3;

/**
 * Get the refresher price for a course based on its parent course price.
 * @param {object} parentCourse - The parent course object with price fields
 * @returns {number} Price for the current platform with 30% discount
 */
export const getRefresherPrice = (parentCourse, overrideCurrency = null) => {
  const basePrice = getPriceForCurrentPlatform(parentCourse, overrideCurrency);
  return Math.round(basePrice * (1 - REFRESHER_DISCOUNT));
};

/**
 * Get the final price for a course, considering refresher and accreditation discounts.
 * Non-accredited pricing uses the explicit non-exemplar price.
 * @param {object} course - Course object (may have parentCourseData attached)
 * @param {object|null} parentCourse - Parent course object (for refresher pricing)
 * @param {'accredited'|'non_accredited'} accreditationType
 * @returns {number} Final price
 */
export const getCoursePrice = (
  course,
  parentCourse,
  accreditationType = "accredited",
  overrideCurrency = null,
) => {
  if (accreditationType === "non_accredited") {
    return getNonExemplarPrice(course, overrideCurrency);
  }

  const basePrice = parentCourse
    ? Math.round(
        getPriceForCurrentPlatform(parentCourse, overrideCurrency) *
          (1 - REFRESHER_DISCOUNT),
      )
    : getPriceForCurrentPlatform(course, overrideCurrency);

  return basePrice;
};

/**
 * Get the correct price field from any object that uses the batch_price_* / bundle_price_* naming.
 * Works for batches, bundles, or any object with currency-suffixed price fields.
 * @param {object} item - Object with price fields (batch_price_inr, bundle_price_cad, etc.)
 * @param {string} prefix - Field prefix, e.g. 'batch_price' or 'bundle_price'
 * @param {string|null} overrideCurrency - Optional currency override
 * @returns {number} Price for the resolved currency
 */
export const getPriceByPrefix = (
  item,
  prefix = "batch_price",
  overrideCurrency = null,
) => {
  const currency = (overrideCurrency || getCurrentCurrency()).toUpperCase();

  if (prefix === "bundle_price") {
    if (currency === "CAD") return item?.bundle_price_cad || 0;
    if (currency === "USD") return item?.bundle_price_usd || 0;
    return item?.bundle_price_inr || 0;
  }

  if (prefix === "price") {
    if (currency === "CAD") return item?.price_cad || 0;
    if (currency === "USD") return item?.price_usd || 0;
    return item?.price_inr || 0;
  }

  if (currency === "CAD") return item?.batch_price_cad || 0;
  if (currency === "USD") return item?.batch_price_usd || 0;
  return item?.batch_price_inr || 0;
};

/**
 * Currency options for partner settings / admin UIs.
 * Single source of truth — import this instead of hardcoding option arrays.
 */
export const CURRENCY_OPTIONS = [
  {
    value: "USD",
    code: "USD",
    label: "USD ($)",
    symbol: "$",
    name: "US Dollar",
    gateway: "Stripe",
  },
  {
    value: "CAD",
    code: "CAD",
    label: "CAD (C$)",
    symbol: "C$",
    name: "Canadian Dollar",
    gateway: "Stripe",
  },
  {
    value: "INR",
    code: "INR",
    label: "INR (₹)",
    symbol: "₹",
    name: "Indian Rupee",
    gateway: "Razorpay",
  },
];
